import React, { createContext, useState, useContext, useCallback } from 'react';
import { searchService } from '../services';

const SearchContext = createContext(null);

export const SearchProvider = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFilter, setSearchFilter] = useState('all'); // all, people, jobs, companies, posts

  // Perform search
  const performSearch = useCallback(async (query, filter = 'all') => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchService.search(query, filter);
      setSearchResults(results);
      setShowSearchResults(true);
      return results;
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      return [];
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setSearchFilter('all');
  };

  // Update query and trigger search
  const updateSearchQuery = (query) => {
    setSearchQuery(query);
    if (query.trim().length >= 2) {
      performSearch(query, searchFilter);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  // Update filter and re-search
  const updateSearchFilter = (filter) => {
    setSearchFilter(filter);
    if (searchQuery.trim().length >= 2) {
      performSearch(searchQuery, filter);
    }
  };

  const value = {
    // State
    searchQuery,
    searchResults,
    showSearchResults,
    isSearching,
    searchFilter,

    // Actions
    setSearchQuery: updateSearchQuery,
    setShowSearchResults,
    setSearchFilter: updateSearchFilter,
    performSearch,
    clearSearch,
  };

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within SearchProvider');
  }
  return context;
};

export default SearchContext;
